/**
 * Модальное окно для запроса разрешения на push-уведомления
 * Требует user gesture для работы на iOS
 */
import { useState, useEffect } from 'react'
import { PushNotificationService } from '../services/pushNotificationService'
import { useWorkerStore } from '../store/workerStore'
import './PushPromptModal.css'

const PROMPT_SHOWN_KEY = 'push_prompt_shown'
const PROMPT_DISMISSED_KEY = 'push_prompt_dismissed_count'
const MAX_DISMISSALS = 3 // После 3 отказов больше не показываем

export function PushPromptModal() {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { employeeId, isOnboarded } = useWorkerStore()

  useEffect(() => {
    // Проверяем нужно ли показывать prompt
    if (!isOnboarded || !employeeId || !PushNotificationService.isSupported()) {
      return
    }

    const permission = Notification.permission
    const hasShownBefore = localStorage.getItem(PROMPT_SHOWN_KEY) === 'true'
    const dismissCount = parseInt(localStorage.getItem(PROMPT_DISMISSED_KEY) || '0', 10)

    // Показываем если:
    // 1. Разрешение еще не запрашивалось (default)
    // 2. ИЛИ разрешение отклонено, но мы еще не показывали prompt (для повторного запроса)
    // 3. И пользователь не отказался уже MAX_DISMISSALS раз
    if (dismissCount < MAX_DISMISSALS) {
      if (permission === 'default' || (permission === 'denied' && !hasShownBefore)) {
        setIsVisible(true)
      }
    }
  }, [isOnboarded, employeeId])

  const handleEnable = async () => {
    setIsLoading(true)
    
    try {
      // Запрашиваем разрешение (это user gesture - клик по кнопке)
      await PushNotificationService.subscribe(employeeId!, true)
      
      // Отмечаем что показали и успешно подписались
      localStorage.setItem(PROMPT_SHOWN_KEY, 'true')
      localStorage.removeItem(PROMPT_DISMISSED_KEY) // Сбрасываем счетчик отказов
      
      setIsVisible(false)
    } catch (error) {
      console.error('[PushPromptModal] Failed to enable notifications:', error)
      
      // Если пользователь отклонил, все равно отмечаем что показали
      localStorage.setItem(PROMPT_SHOWN_KEY, 'true')
      setIsVisible(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDismiss = () => {
    // Увеличиваем счетчик отказов
    const dismissCount = parseInt(localStorage.getItem(PROMPT_DISMISSED_KEY) || '0', 10)
    localStorage.setItem(PROMPT_DISMISSED_KEY, String(dismissCount + 1))
    localStorage.setItem(PROMPT_SHOWN_KEY, 'true')
    
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="push-prompt-overlay" onClick={handleDismiss}>
      <div className="push-prompt-modal" onClick={e => e.stopPropagation()}>
        <div className="push-prompt-icon">🔔</div>
        <h2 className="push-prompt-title">Включить уведомления?</h2>
        <p className="push-prompt-description">
          Получайте важные сообщения от администратора: изменения графика, новости и объявления.
        </p>
        
        <div className="push-prompt-actions">
          <button 
            className="push-prompt-btn push-prompt-btn-primary" 
            onClick={handleEnable}
            disabled={isLoading}
          >
            {isLoading ? 'Подключение...' : 'Включить'}
          </button>
          <button 
            className="push-prompt-btn push-prompt-btn-secondary" 
            onClick={handleDismiss}
            disabled={isLoading}
          >
            Не сейчас
          </button>
        </div>
        
        <p className="push-prompt-hint">
          Вы всегда сможете изменить это в профиле
        </p>
      </div>
    </div>
  )
}
